import { useEffect, useState } from 'react';
import { DEFAULT_MEDIA_ASPECT_RATIO, parseMediaAspectRatioOption } from './mediaFit';
import { MEDIA_KINDS, normalizeMediaSource } from './mediaSource';
import { useImageAspectRatio } from './useImageAspectRatio';

/** Ratio vidéo via métadonnées (si pas de poster). */
function useVideoAspectRatio(src, fallback = DEFAULT_MEDIA_ASPECT_RATIO) {
  const [ratio, setRatio] = useState(fallback);

  useEffect(() => {
    if (!src || typeof src !== 'string') {
      setRatio(fallback);
      return undefined;
    }

    let cancelled = false;
    const video = document.createElement('video');
    video.preload = 'metadata';

    const onMeta = () => {
      if (!cancelled && video.videoWidth > 0 && video.videoHeight > 0) {
        setRatio(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener('loadedmetadata', onMeta, { once: true });
    video.addEventListener('error', () => {
      if (!cancelled) setRatio(fallback);
    }, { once: true });
    video.src = src;

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onMeta);
      video.src = '';
    };
  }, [src, fallback]);

  return ratio;
}

/** Ratio largeur/hauteur selon type de média (image, poster vidéo, métadonnées vidéo). */
export function useMediaAspectRatio(media, fallback = DEFAULT_MEDIA_ASPECT_RATIO) {
  const resolved = normalizeMediaSource(media);
  const imageProbe = resolved.kind === MEDIA_KINDS.image
    ? resolved.src
    : (resolved.kind === MEDIA_KINDS.video && resolved.poster ? resolved.poster : '');
  const ratioFromImage = useImageAspectRatio(imageProbe, fallback);
  const ratioFromVideo = useVideoAspectRatio(
    resolved.kind === MEDIA_KINDS.video && !resolved.poster ? resolved.src : '',
    fallback,
  );

  if (resolved.kind === MEDIA_KINDS.video) {
    return resolved.poster ? ratioFromImage : ratioFromVideo;
  }
  if (resolved.kind === MEDIA_KINDS.image) {
    return ratioFromImage;
  }
  return fallback;
}

/**
 * Ratio effectif d'un cadre média : préréglage utilisateur (16/9…) sinon
 * ratio détecté du fichier, sinon fallback (16/9 pour les embeds).
 */
export function useResolvedRatio(media, mediaAspectRatio, fallback) {
  const detected = useMediaAspectRatio(media, fallback);
  const preset = parseMediaAspectRatioOption(mediaAspectRatio);
  if (preset != null) return preset;
  const resolved = normalizeMediaSource(media);
  if (resolved.kind === MEDIA_KINDS.embed) return 16 / 9;
  return detected ?? fallback ?? DEFAULT_MEDIA_ASPECT_RATIO;
}
